import { useLayoutEffect } from 'react';
import styled from 'styled-components';

import {
  ReferralDeterminantsFormValues,
  ReferralFormMeta,
  SelectValue,
} from 'components/modals/referral/SendReferralModal';
import Button from 'components/shared/Button';
import DatePicker from 'components/shared/form/DatePicker';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import { Actions } from 'components/shared/StandardModal';
import { useForm } from 'context/form';
import { useTranslation } from 'context/translations';
import { formatPhone10Digits } from 'lib/phone';
import colors from 'styles/theme/colors';
import devices from 'styles/theme/devices';
import { BodySmall } from 'styles/typography';

import { ReferralSteps, STATES, USState } from './helpers';

type EditDeterminantsStepProps = {
  initialPhoneNumber: string;
};

export default function EditDeterminantsStep({ initialPhoneNumber }: EditDeterminantsStepProps) {
  const { errors, setValue, values, validateField, valid, meta, setMeta, submitForm } = useForm<
    ReferralDeterminantsFormValues,
    ReferralFormMeta
  >();

  const { translate } = useTranslation();

  useLayoutEffect(() => {
    setValue('phone', formatPhone10Digits(initialPhoneNumber));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isMultiStep = meta.steps && meta.steps.length > 1;

  return (
    <>
      <>
        <InputGroup title={`Expected ${translate('Discharge')} Date (optional)`}>
          <DatePicker
            minDate={new Date()}
            selected={values.expectedDischargeDate ?? null}
            onChange={(date) => {
              setValue('expectedDischargeDate', date);
            }}
            $hasError={!!errors.expectedDischargeDate}
            onBlur={() => validateField('expectedDischargeDate', values)}
          />
        </InputGroup>
        <HR />
        <BodySmall style={{ margin: '24px 0px' }}>
          Review and edit the patient's demographic information below to ensure it is accurate and up-to-date:
        </BodySmall>
        <ContactInfoContainer>
          <InputGroup title='Street 1'>
            <Input
              value={values.address1 ?? ''}
              onChange={(e) => setValue('address1', e.target.value)}
              $hasError={!!errors.address1}
              onBlur={() => validateField('address1', values)}
              placeholder='Street 1'
            />
          </InputGroup>
          <InputGroup title='Street 2'>
            <Input
              value={values.address2 ?? ''}
              onChange={(e) => setValue('address2', e.target.value)}
              $hasError={!!errors.address2}
              onBlur={() => validateField('address2', values)}
              placeholder='Street 2'
            />
          </InputGroup>
          <CityStateZip>
            <InputGroup title='City'>
              <Input
                value={values.city ?? ''}
                onChange={(e) => setValue('city', e.target.value)}
                $hasError={!!errors.city}
                onBlur={() => validateField('city', values)}
                placeholder='City'
              />
            </InputGroup>
            <InputGroup title='State'>
              <Select<SelectValue>
                value={values.state ? { label: values.state, value: values.state } : null}
                onChange={(option) => setValue('state', option.value as USState)}
                hasError={!!errors.state}
                onBlur={() => validateField('state', values)}
                options={[...STATES].map((s) => ({ label: s, value: s }))}
                getOptionValue={(o) => o.value}
                getOptionLabel={(o) => o.label}
                placeholder='State'
                typeToSearchText='State'
              />
            </InputGroup>
            <InputGroup title='Zipcode' error={errors.zip}>
              <Input
                value={values.zip ?? ''}
                onChange={(e) => setValue('zip', e.target.value)}
                $hasError={!!errors.zip}
                onBlur={() => validateField('zip', values)}
                placeholder='Zipcode'
              />
            </InputGroup>
          </CityStateZip>
          <InputGroup title='Phone Number' error={errors.phone}>
            <Input
              value={values.phone ?? ''}
              onChange={(e) => setValue('phone', formatPhone10Digits(e.target.value))}
              $hasError={!!errors.phone}
              onBlur={() => validateField('phone', values)}
              type='tel'
              placeholder='Phone Number'
            />
          </InputGroup>
        </ContactInfoContainer>
      </>
      <StyledActions>
        <Button
          variant='ghost'
          type='button'
          onClick={() => {
            if (isMultiStep) {
              meta.onPreviousStep?.(setMeta, meta.currentStep as ReferralSteps);
            } else {
              meta.onCancel?.();
            }
          }}>
          {isMultiStep ? 'Back' : 'Cancel'}
        </Button>
        <Button
          type='button'
          data-cy='clickable'
          disabled={!valid}
          loading={meta.isSubmitting}
          onClick={() => submitForm()}>
          {meta.submitText}
        </Button>
      </StyledActions>
    </>
  );
}

const CityStateZip = styled.div`
  display: flex;
  gap: 10px;

  > :first-child {
    flex-grow: 1;
    flex-shrink: 0;
    width: 240px;
  }

  @media not ${devices.desktop} {
    flex-direction: column;
    gap: 0px;

    > *:not(:last-child) {
      margin-bottom: -10px;
    }

    > :first-child {
      width: auto;
    }
  }
`;

const StyledActions = styled(Actions)`
  margin-top: 20px;
`;

const HR = styled.hr`
  border: none;
  height: 1px;
  background-color: ${colors.black10};
`;

const ContactInfoContainer = styled.div`
  display: flex;
  flex-direction: column;

  > * {
    margin-bottom: -10px;
  }
`;
